import { createStore, applyMiddleware, compose } from 'redux';
import { createLogger } from 'redux-logger';
import createSagaMiddleware from 'redux-saga';
import rootReducer from 'reducers/root';
import rootSaga from 'sagas';
import { theme } from 'constants/styled-theme';

const combine =
  reducers =>
  (state = {}, action) => {
    const keys = Object.keys(reducers);
    const arr = keys.reduce(
      (accumulator, key) => ({
        ...accumulator,
        ...reducers[key](state, action)
      }),
      {}
    );
    return arr;
  };
const createReducer = asyncReducers =>
  combine({
    rootReducer,
    ...asyncReducers
  });

const createSagaInjector = (runSaga, root) => {
  // Create a dictionary to keep track of injected sagas
  const injectedSagas = new Map();

  const isInjected = key => injectedSagas.has(key);

  const injectSaga = (key, saga) => {
    // We won't run saga if it is already injected
    if (isInjected(key)) return;

    // Sagas return task when they executed, which can be used
    // to cancel them
    const task = runSaga(saga);

    // Save the task if we want to cancel it in the future
    injectedSagas.set(key, task);
  };

  // Inject the root saga as it a staticlly loaded file,
  injectSaga('root', root);

  return injectSaga;
};

let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;

window.addEventListener('scroll', () => {
  const body = document.querySelector('body');
  const isPageScrolled =
    window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
  if (!isPageScrolled) {
    body.classList.remove('scrolled');
    body.classList.remove('fullyScrolled');
    return;
  }
  body.classList.add('scrolled');
  const yOffsetOfBottomOfWindow = window.pageYOffset + window.innerHeight;
  const footerHeight = +theme.footerHeight.replace('px', '');
  const marginOfError = 2;
  const isFullyScrolled = yOffsetOfBottomOfWindow + marginOfError >= document.body.offsetHeight;
  const yOffsetOfTopOfFooter = document.body.offsetHeight - footerHeight;
  const isBottomOfWindowAboveFooter =
    yOffsetOfBottomOfWindow - marginOfError < yOffsetOfTopOfFooter;
  const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const isScrollingBackUp = currentScrollTop < lastScrollTop;
  if (isFullyScrolled && !isScrollingBackUp) {
    body.classList.add('fullyScrolled');
  } else if (isBottomOfWindowAboveFooter && isScrollingBackUp) {
    body.classList.remove('fullyScrolled');
  }
  lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop;
});

const logger = createLogger({ collapsed: true });

const sagaMiddleware = createSagaMiddleware();
/* eslint-disable */
const composeEnhancers =
  __ENV !== 'production' &&
  typeof window === 'object' &&
  window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
    : compose;
/* eslint-enable */

const middleware = [sagaMiddleware];
if (__ENV !== 'production') {
  middleware.unshift(logger);
}

const enhancer = composeEnhancers(applyMiddleware(...middleware));

export const store = createStore(rootReducer, enhancer);

store.asyncReducers = {};
store.injectReducer = (key, asyncReducer) => {
  store.asyncReducers[key] = asyncReducer;
  store.replaceReducer(createReducer(store.asyncReducers));
};

store.injectSaga = createSagaInjector(sagaMiddleware.run, rootSaga);

if (__ENV === 'development' || __ENV === 'localhost') {
  window.store = store;
}

export default store;
